import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './CouponManagement.css';

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    universal: 0,
    restaurant: 0,
    active: 0,
    expired: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('all'); // all, universal, restaurant
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const emptyForm = {
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderValue: '',
    maxOrderValue: '',
    maxDiscount: '',
    validFrom: '',
    validUntil: '',
    usageLimit: '',
    oneTimePerUser: false,
    isFirstOrderFreeDelivery: false,
    // Advanced targeting / classifications
    appliesTo: 'subtotal',
    autoApply: false,
    audience: 'all',
    perUserLimit: '',
    minItems: '',
    paymentMethods: [],
    activeDays: [],
    startHour: '',
    endHour: '',
    // BOGO / item-level
    couponKind: 'standard',
    applicableCategories: '',
    buyQuantity: '1',
    getQuantity: '1',
    getDiscountPercent: '100',
    // Specific-customer targeting (array of User IDs; empty = available to everyone)
    assignedUsers: [],
    // Scope + funding: target ONE restaurant (restaurantId), a LIST of
    // restaurants (restaurantIds), or all (both empty = universal).
    restaurantId: '',
    restaurantIds: [],
    fundedBy: 'platform',
    // For fundedBy 'shared': % of the discount the RESTAURANT bears (platform bears the rest).
    restaurantSharePercent: 50
  };
  const [formData, setFormData] = useState(emptyForm);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantListQuery, setRestaurantListQuery] = useState(''); // filter for the multi-restaurant picker

  // Coupon assignee picker: selected customers (with names for chips) + live search.
  const [assignedCustomers, setAssignedCustomers] = useState([]); // [{_id, name, phone}]
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [custSearching, setCustSearching] = useState(false);

  // "Target by segment": bulk-resolve a customer segment into the assignee picker.
  const [segFilter, setSegFilter] = useState({ discountSensitivity: [], rfmSegment: [], onlyRestaurantCustomers: false });
  const [segPreview, setSegPreview] = useState(null); // { count, customers }
  const [segLoading, setSegLoading] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, [filter, statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getAllRestaurants({ limit: 1000 });
        setRestaurants(res.data?.data?.restaurants || []);
      } catch (e) {
        // Non-fatal: the restaurant dropdown just stays empty (universal still works).
      }
    })();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.type = filter;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await adminAPI.getAllCoupons(params);
      setCoupons(response.data.data.coupons);
      setStats(response.data.data.stats);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch coupons' });
      console.error('Failed to fetch coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAnalytics = async () => {
    const next = !showAnalytics;
    setShowAnalytics(next);
    if (next && !analytics) {
      try {
        setAnalyticsLoading(true);
        const res = await adminAPI.getCouponAnalytics();
        setAnalytics(res.data.data);
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to load coupon analytics' });
      } finally {
        setAnalyticsLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Scope change (single-restaurant dropdown): picking one restaurant clears any
  // multi-restaurant list (mutually exclusive). Clearing to "All restaurants"
  // with no list = universal → forces platform funding.
  const handleScopeChange = (e) => {
    const restaurantId = e.target.value;
    setFormData(prev => {
      const isMulti = !restaurantId && (prev.restaurantIds || []).length > 0;
      return {
        ...prev,
        restaurantId,
        restaurantIds: restaurantId ? [] : prev.restaurantIds,
        fundedBy: (restaurantId || isMulti) ? prev.fundedBy : 'platform'
      };
    });
  };

  // Toggle a restaurant in the multi-restaurant list. Selecting any list member
  // clears the single-restaurant dropdown (mutually exclusive).
  const toggleRestaurantInList = (id) => {
    setFormData(prev => {
      const set = new Set((prev.restaurantIds || []).map(String));
      set.has(String(id)) ? set.delete(String(id)) : set.add(String(id));
      const restaurantIds = [...set];
      return {
        ...prev,
        restaurantId: restaurantIds.length ? '' : prev.restaurantId,
        restaurantIds,
        fundedBy: restaurantIds.length ? prev.fundedBy : (prev.restaurantId ? prev.fundedBy : 'platform')
      };
    });
  };

  // Toggle a value in an array-valued field (paymentMethods, activeDays).
  const toggleArrayValue = (field, value) => {
    setFormData(prev => {
      const set = new Set(prev[field] || []);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...prev, [field]: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const data = {
        ...formData,
        discountValue: formData.isFirstOrderFreeDelivery ? 0 : parseFloat(formData.discountValue),
        minOrderValue: formData.minOrderValue ? parseFloat(formData.minOrderValue) : 0,
        maxOrderValue: formData.maxOrderValue ? parseFloat(formData.maxOrderValue) : 0,
        maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        oneTimePerUser: formData.isFirstOrderFreeDelivery ? true : (formData.oneTimePerUser || false),
        isFirstOrderFreeDelivery: formData.isFirstOrderFreeDelivery || false,
        // Advanced targeting
        appliesTo: formData.appliesTo || 'subtotal',
        autoApply: formData.autoApply || false,
        audience: formData.audience || 'all',
        perUserLimit: formData.perUserLimit ? parseInt(formData.perUserLimit) : null,
        minItems: formData.minItems ? parseInt(formData.minItems) : 0,
        paymentMethods: formData.paymentMethods || [],
        activeDays: (formData.activeDays || []).map(Number),
        startHour: formData.startHour !== '' ? parseInt(formData.startHour) : null,
        endHour: formData.endHour !== '' ? parseInt(formData.endHour) : null,
        // BOGO / item-level
        couponKind: formData.couponKind || 'standard',
        applicableCategories: (formData.applicableCategories || '')
          .split(',').map(s => s.trim()).filter(Boolean),
        buyQuantity: formData.buyQuantity ? parseInt(formData.buyQuantity) : 1,
        getQuantity: formData.getQuantity ? parseInt(formData.getQuantity) : 1,
        getDiscountPercent: formData.getDiscountPercent !== '' ? parseInt(formData.getDiscountPercent) : 100,
        // Specific-customer targeting (array of User IDs; empty = everyone)
        assignedUsers: formData.assignedUsers || [],
        // Scope + funding: single restaurant, a list, or all (universal).
        // A single restaurant takes precedence over the list; both empty = universal.
        restaurantId: formData.restaurantId || null,
        restaurantIds: formData.restaurantId ? [] : (formData.restaurantIds || []),
        fundedBy: (formData.restaurantId || (formData.restaurantIds || []).length) ? formData.fundedBy : 'platform',
        // Only meaningful when fundedBy === 'shared'; harmless otherwise (backend ignores it).
        restaurantSharePercent: Math.max(0, Math.min(100, Math.round(Number(formData.restaurantSharePercent) || 0)))
      };
      // Guard: shared funding is invalid for delivery / free-delivery promos — fall back to platform.
      if (data.fundedBy === 'shared' && (formData.isFirstOrderFreeDelivery || formData.appliesTo === 'delivery')) {
        data.fundedBy = 'platform';
      }

      if (editingCoupon) {
        await adminAPI.updateCoupon(editingCoupon._id, data);
        setMessage({ type: 'success', text: 'Coupon updated successfully!' });
      } else {
        await adminAPI.createUniversalCoupon(data);
        setMessage({ type: 'success', text: formData.isFirstOrderFreeDelivery
          ? 'First Order Free Delivery coupon created successfully!'
          : 'Universal coupon created successfully!' });
      }

      resetForm();
      fetchCoupons();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save coupon'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue.toString(),
      minOrderValue: coupon.minOrderValue.toString(),
      maxOrderValue: coupon.maxOrderValue ? coupon.maxOrderValue.toString() : '',
      maxDiscount: coupon.maxDiscount?.toString() || '',
      validFrom: coupon.validFrom.split('T')[0],
      validUntil: coupon.validUntil.split('T')[0],
      usageLimit: coupon.usageLimit?.toString() || '',
      oneTimePerUser: coupon.oneTimePerUser || false,
      isFirstOrderFreeDelivery: coupon.isFirstOrderFreeDelivery || false,
      appliesTo: coupon.appliesTo || 'subtotal',
      autoApply: coupon.autoApply || false,
      audience: coupon.audience || 'all',
      perUserLimit: coupon.perUserLimit?.toString() || '',
      minItems: coupon.minItems?.toString() || '',
      paymentMethods: coupon.paymentMethods || [],
      activeDays: (coupon.activeDays || []).map(Number),
      startHour: coupon.startHour != null ? coupon.startHour.toString() : '',
      endHour: coupon.endHour != null ? coupon.endHour.toString() : '',
      couponKind: coupon.couponKind || 'standard',
      applicableCategories: (coupon.applicableCategories || []).join(', '),
      buyQuantity: (coupon.buyQuantity ?? 1).toString(),
      getQuantity: (coupon.getQuantity ?? 1).toString(),
      getDiscountPercent: (coupon.getDiscountPercent ?? 100).toString(),
      assignedUsers: (coupon.assignedUsers || []).map(u => (u && u._id) ? u._id : u),
      restaurantId: (coupon.restaurantId && (coupon.restaurantId._id || coupon.restaurantId)) || '',
      restaurantIds: (coupon.restaurantIds || []).map(r => (r && r._id) ? r._id : r),
      fundedBy: coupon.fundedBy || ((coupon.restaurantId && !coupon.isUniversal) ? 'restaurant' : 'platform'),
      restaurantSharePercent: (coupon.restaurantSharePercent != null) ? coupon.restaurantSharePercent : 50
    });
    // Populate the assignee chips from the populated {_id,name,phone} objects.
    setAssignedCustomers((coupon.assignedUsers || []).map(u => ({
      _id: (u && u._id) ? u._id : u, name: (u && u.name) || '', phone: (u && u.phone) || ''
    })));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await adminAPI.deleteCoupon(id);
        setMessage({ type: 'success', text: 'Coupon deleted successfully!' });
        fetchCoupons();
      } catch (error) {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to delete coupon'
        });
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await adminAPI.toggleCouponStatus(id);
      fetchCoupons();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to toggle coupon status'
      });
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingCoupon(null);
    setShowForm(false);
    setAssignedCustomers([]);
    setCustQuery('');
    setCustResults([]);
  };

  // ── Coupon assignee picker handlers ──────────────────────────────────────
  const searchCustomers = async (q) => {
    setCustQuery(q);
    if (!q || q.trim().length < 2) { setCustResults([]); return; }
    setCustSearching(true);
    try {
      const res = await adminAPI.searchCouponCustomers(q.trim());
      setCustResults(res.data?.data?.customers || []);
    } catch (e) {
      setCustResults([]);
    } finally {
      setCustSearching(false);
    }
  };

  const addAssignedCustomer = (c) => {
    const id = c._id;
    if (!id || (formData.assignedUsers || []).includes(id)) return;
    setAssignedCustomers(prev => [...prev, { _id: id, name: c.name || '', phone: c.phone || '' }]);
    setFormData(prev => ({ ...prev, assignedUsers: [...(prev.assignedUsers || []), id] }));
    setCustQuery('');
    setCustResults([]);
  };

  const removeAssignedCustomer = (id) => {
    setAssignedCustomers(prev => prev.filter(c => c._id !== id));
    setFormData(prev => ({ ...prev, assignedUsers: (prev.assignedUsers || []).filter(u => u !== id) }));
  };

  // ── "Target by segment" handlers ─────────────────────────────────────────
  const toggleSegValue = (key, value) => {
    setSegFilter(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
    setSegPreview(null);
  };

  const previewSegment = async () => {
    if (!segFilter.discountSensitivity.length && !segFilter.rfmSegment.length) return;
    setSegLoading(true);
    try {
      const res = await adminAPI.resolveCouponSegment({
        discountSensitivity: segFilter.discountSensitivity,
        rfmSegment: segFilter.rfmSegment,
        restaurantId: formData.restaurantId || null,
        onlyRestaurantCustomers: segFilter.onlyRestaurantCustomers,
      });
      setSegPreview(res.data?.data || { count: 0, customers: [] });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to resolve segment' });
      setSegPreview(null);
    } finally {
      setSegLoading(false);
    }
  };

  const addSegmentCustomers = () => {
    if (!segPreview?.customers?.length) return;
    const existing = new Set(formData.assignedUsers || []);
    const toAdd = segPreview.customers.filter(c => !existing.has(c._id));
    if (toAdd.length) {
      setAssignedCustomers(prev => [...prev, ...toAdd.map(c => ({ _id: c._id, name: c.name, phone: c.phone }))]);
      setFormData(prev => ({ ...prev, assignedUsers: [...(prev.assignedUsers || []), ...toAdd.map(c => c._id)] }));
    }
    setMessage({ type: 'success', text: `Added ${toAdd.length} customer(s) to the coupon.` });
    setSegPreview(null);
    setSegFilter({ discountSensitivity: [], rfmSegment: [], onlyRestaurantCustomers: false });
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  if (loading && coupons.length === 0) {
    return (
      <div className="admin-layout">
        <Navbar />
        <div className="admin-content">
          <div className="loading">Loading coupons...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Navbar />
      <div className="admin-content">
        <div className="admin-header">
          <h1>Coupon Management</h1>
          <p>Create and manage universal coupons for all restaurants</p>
        </div>

        <div className="coupon-container">
          {/* Stats Card */}
          <div className="coupon-card stats-card">
            <h2>Coupon Statistics</h2>
            <p className="card-description">Overview of all coupons in the system</p>

            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-icon total-icon">🎫</div>
                <div className="stat-info">
                  <div className="stat-label">Total Coupons</div>
                  <div className="stat-value highlight">{stats.total}</div>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-icon universal-icon">🌍</div>
                <div className="stat-info">
                  <div className="stat-label">Universal</div>
                  <div className="stat-value">{stats.universal}</div>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-icon restaurant-icon">🍽️</div>
                <div className="stat-info">
                  <div className="stat-label">Restaurant</div>
                  <div className="stat-value">{stats.restaurant}</div>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-icon active-icon">✅</div>
                <div className="stat-info">
                  <div className="stat-label">Active</div>
                  <div className="stat-value">{stats.active}</div>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-icon expired-icon">⏰</div>
                <div className="stat-info">
                  <div className="stat-label">Expired</div>
                  <div className="stat-value">{stats.expired}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="coupon-card actions-card">
            <div className="actions-row">
              <button
                onClick={() => setShowForm(true)}
                className="btn btn-primary"
                disabled={showForm}
              >
                + Create Coupon
              </button>

              <button
                onClick={toggleAnalytics}
                className="btn btn-secondary"
              >
                {showAnalytics ? 'Hide Analytics' : '📊 Analytics'}
              </button>

              <div className="filters-row">
                <div className="filter-group">
                  <label>Type:</label>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Coupons</option>
                    <option value="universal">Universal Only</option>
                    <option value="restaurant">Restaurant Only</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Status:</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Create/Edit Form */}
          {showForm && (
            <div className="coupon-card form-card">
              <h2>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h2>
              <p className="card-description">
                {editingCoupon
                  ? 'Update the coupon details below'
                  : 'Universal (all restaurants) or restaurant-specific — platform- or restaurant-funded'}
              </p>

              <form onSubmit={handleSubmit}>
                {/* First Order Free Delivery Toggle */}
                <div className="form-group checkbox-group first-order-toggle">
                  <label className="checkbox-label highlight-label">
                    <input
                      type="checkbox"
                      name="isFirstOrderFreeDelivery"
                      checked={formData.isFirstOrderFreeDelivery}
                      onChange={handleInputChange}
                      className="checkbox-input"
                    />
                    <span className="checkbox-text">
                      <span className="toggle-icon">🚀</span> First Order Free Delivery
                    </span>
                  </label>
                  <span className="field-hint">
                    Enable this to create a special coupon that gives free delivery on each user's first order.
                    This coupon will auto-apply when a new user places their first order.
                  </span>
                </div>

                {formData.isFirstOrderFreeDelivery && (
                  <div className="info-banner success-banner">
                    <span className="banner-icon">✅</span>
                    <div className="banner-content">
                      <strong>First Order Free Delivery Mode</strong>
                      <p>This coupon will automatically waive the delivery fee for each user's first order.
                      The coupon is one-time per user and auto-applies at checkout.</p>
                    </div>
                  </div>
                )}

                {/* Scope + funding: target one restaurant, a list, or all — and choose who absorbs the discount */}
                {(() => {
                  const listCount = (formData.restaurantIds || []).length;
                  const isMulti = !formData.restaurantId && listCount > 0;
                  const canFund = !!formData.restaurantId || isMulti;
                  // Shared funding draws the restaurant's share from FOOD revenue, so it
                  // only makes sense for a subtotal coupon — not a delivery / free-delivery promo.
                  const canShare = canFund && !formData.isFirstOrderFreeDelivery && formData.appliesTo !== 'delivery';
                  const effectiveFunded = canFund ? formData.fundedBy : 'platform';
                  return (
                <>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Applies to</label>
                    <select
                      name="restaurantId"
                      value={formData.restaurantId}
                      onChange={handleScopeChange}
                      className="form-control"
                    >
                      <option value="">All restaurants (Universal){listCount > 0 ? ' — overridden by the list below' : ''}</option>
                      {restaurants.map(r => (
                        <option key={r._id} value={r._id}>{r.name}</option>
                      ))}
                    </select>
                    <span className="field-hint">
                      Pick one restaurant, or keep "All restaurants" and choose a list below, or leave both empty for a universal coupon.
                    </span>
                  </div>

                  <div className="form-group">
                    <label>Funded by</label>
                    <select
                      name="fundedBy"
                      value={effectiveFunded === 'shared' && !canShare ? 'platform' : effectiveFunded}
                      onChange={handleInputChange}
                      className="form-control"
                      disabled={!canFund}
                    >
                      <option value="platform">Platform — FoodWinc absorbs the discount</option>
                      <option value="restaurant">Restaurant — the restaurant absorbs the discount</option>
                      {canShare && <option value="shared">Shared — split between platform and restaurant</option>}
                    </select>
                    <span className="field-hint">
                      {canFund
                        ? (formData.fundedBy === 'shared' && canShare
                            ? 'The discount cost is split: the restaurant bears the share below, FoodWinc bears the rest. Only for food (subtotal) coupons; shared coupons are not combined with other coupons.'
                            : (isMulti
                                ? 'For a list, restaurant-funded charges each redemption to that order’s own restaurant; platform-funded is borne by FoodWinc.'
                                : 'Who bears the discount cost. Restaurant-funded reduces that restaurant’s payout; platform-funded is borne by FoodWinc.'))
                        : 'Universal coupons are always platform-funded.'}
                    </span>
                  </div>

                  {formData.fundedBy === 'shared' && canShare && (
                    <div className="form-group">
                      <label>Restaurant's share of the discount (%)</label>
                      <input
                        type="number"
                        name="restaurantSharePercent"
                        min="0"
                        max="100"
                        step="1"
                        value={formData.restaurantSharePercent}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                      <span className="field-hint">
                        Restaurant bears {Math.max(0, Math.min(100, Number(formData.restaurantSharePercent) || 0))}% of every
                        redemption; FoodWinc bears the remaining {100 - Math.max(0, Math.min(100, Number(formData.restaurantSharePercent) || 0))}%.
                      </span>
                    </div>
                  )}
                </div>

                {/* Multi-restaurant picker — only when no single restaurant is chosen */}
                {!formData.restaurantId && (
                  <div className="form-group">
                    <label>Or apply to a list of restaurants {listCount > 0 && <span style={{ color: '#3730a3' }}>({listCount} selected)</span>}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Filter restaurants…"
                      value={restaurantListQuery}
                      onChange={(e) => setRestaurantListQuery(e.target.value)}
                      style={{ marginBottom: 8 }}
                    />
                    <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                      {restaurants
                        .filter(r => !restaurantListQuery || (r.name || '').toLowerCase().includes(restaurantListQuery.toLowerCase()))
                        .map(r => {
                          const checked = (formData.restaurantIds || []).map(String).includes(String(r._id));
                          return (
                            <label key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer' }}>
                              <input type="checkbox" checked={checked} onChange={() => toggleRestaurantInList(r._id)} />
                              <span>{r.name}</span>
                            </label>
                          );
                        })}
                      {restaurants.length === 0 && <span className="field-hint">No restaurants loaded.</span>}
                    </div>
                    <span className="field-hint">
                      Tick two or more restaurants to make this coupon valid at exactly those outlets. Leave empty for a universal coupon.
                    </span>
                    {listCount > 0 && (
                      <button type="button" className="btn-link" style={{ marginTop: 6 }}
                        onClick={() => setFormData(prev => ({ ...prev, restaurantIds: [], fundedBy: 'platform' }))}>
                        Clear list
                      </button>
                    )}
                  </div>
                )}
                </>
                  );
                })()}

                <div className="form-grid">
                  <div className="form-group">
                    <label>Coupon Code *</label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder={formData.isFirstOrderFreeDelivery ? 'e.g., FIRSTFREE' : 'e.g., SAVE20'}
                      className="form-control"
                      style={{ textTransform: 'uppercase' }}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Description *</label>
                    <input
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={formData.isFirstOrderFreeDelivery
                        ? 'e.g., Free delivery on your first order!'
                        : 'e.g., Get 20% off on orders above Rs.500'}
                      className="form-control"
                      required
                    />
                  </div>

                  {!formData.isFirstOrderFreeDelivery && (
                    <>
                      <div className="form-group">
                        <label>Discount Type *</label>
                        <select
                          name="discountType"
                          value={formData.discountType}
                          onChange={handleInputChange}
                          className="form-control"
                          required
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (Rs.)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Discount Value *</label>
                        <input
                          type="number"
                          name="discountValue"
                          value={formData.discountValue}
                          onChange={handleInputChange}
                          placeholder={formData.discountType === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                          className="form-control"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>Minimum Order Value (Rs.)</label>
                    <input
                      type="number"
                      name="minOrderValue"
                      value={formData.minOrderValue}
                      onChange={handleInputChange}
                      placeholder="e.g., 500"
                      className="form-control"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>Maximum Order Value (Rs.)</label>
                    <input
                      type="number"
                      name="maxOrderValue"
                      value={formData.maxOrderValue}
                      onChange={handleInputChange}
                      placeholder="0 = no limit; e.g. 200 shows only for carts up to Rs.200"
                      className="form-control"
                      min="0"
                      step="0.01"
                    />
                    <small style={{ color: '#888' }}>Coupon is hidden + blocked once the cart subtotal goes above this. Leave 0 for no cap.</small>
                  </div>

                  {!formData.isFirstOrderFreeDelivery && formData.discountType === 'percentage' && (
                    <div className="form-group">
                      <label>Max Discount (Rs.)</label>
                      <input
                        type="number"
                        name="maxDiscount"
                        value={formData.maxDiscount}
                        onChange={handleInputChange}
                        placeholder="e.g., 200"
                        className="form-control"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Valid From *</label>
                    <input
                      type="date"
                      name="validFrom"
                      value={formData.validFrom}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Valid Until *</label>
                    <input
                      type="date"
                      name="validUntil"
                      value={formData.validUntil}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Usage Limit (leave empty for unlimited)</label>
                    <input
                      type="number"
                      name="usageLimit"
                      value={formData.usageLimit}
                      onChange={handleInputChange}
                      placeholder="e.g., 100"
                      className="form-control"
                      min="1"
                    />
                  </div>

                  {!formData.isFirstOrderFreeDelivery && (
                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="oneTimePerUser"
                          checked={formData.oneTimePerUser}
                          onChange={handleInputChange}
                          className="checkbox-input"
                        />
                        <span className="checkbox-text">One-time per user</span>
                      </label>
                      <span className="field-hint">Each user can only use this coupon once ever</span>
                    </div>
                  )}

                  {!formData.isFirstOrderFreeDelivery && (
                    <>
                      <div className="form-group">
                        <label>Coupon Type</label>
                        <select name="couponKind" value={formData.couponKind} onChange={handleInputChange} className="form-control">
                          <option value="standard">Standard (order discount)</option>
                          <option value="item">Item-level (off specific items)</option>
                          <option value="bogo">BOGO (buy X get Y)</option>
                        </select>
                        <span className="field-hint">Standard = whole order; Item/BOGO target specific items</span>
                      </div>

                      {(formData.couponKind === 'item' || formData.couponKind === 'bogo') && (
                        <div className="form-group">
                          <label>Qualifying categories (comma-separated, empty = all)</label>
                          <input
                            type="text"
                            name="applicableCategories"
                            value={formData.applicableCategories}
                            onChange={handleInputChange}
                            placeholder="e.g., Pizza, Beverages"
                            className="form-control"
                          />
                          <span className="field-hint">Which menu categories the offer applies to</span>
                        </div>
                      )}

                      {formData.couponKind === 'bogo' && (
                        <div className="form-group">
                          <label>BOGO rule</label>
                          <div className="hour-row">
                            <input type="number" name="buyQuantity" value={formData.buyQuantity} onChange={handleInputChange} placeholder="Buy" className="form-control" min="1" />
                            <span className="hour-sep">get</span>
                            <input type="number" name="getQuantity" value={formData.getQuantity} onChange={handleInputChange} placeholder="Get" className="form-control" min="1" />
                            <span className="hour-sep">at</span>
                            <input type="number" name="getDiscountPercent" value={formData.getDiscountPercent} onChange={handleInputChange} placeholder="% off" className="form-control" min="0" max="100" />
                            <span className="hour-sep">% off</span>
                          </div>
                          <span className="field-hint">e.g. Buy 1 get 1 at 100% off = free. Cheapest qualifying items are discounted.</span>
                        </div>
                      )}

                      {formData.couponKind !== 'bogo' && (
                      <div className="form-group">
                        <label>Applies To</label>
                        <select name="appliesTo" value={formData.appliesTo} onChange={handleInputChange} className="form-control" disabled={formData.couponKind === 'item'}>
                          <option value="subtotal">Food subtotal</option>
                          <option value="delivery">Delivery fee</option>
                          <option value="all">Whole order</option>
                        </select>
                        <span className="field-hint">{formData.couponKind === 'item' ? 'Item coupons always apply to the qualifying items' : 'What the discount is applied to'}</span>
                      </div>
                      )}

                      <div className="form-group">
                        <label>Audience</label>
                        <select name="audience" value={formData.audience} onChange={handleInputChange} className="form-control">
                          <option value="all">All customers</option>
                          <option value="new">New users only (first order)</option>
                          <option value="existing">Existing users only</option>
                        </select>
                        <span className="field-hint">Target new vs returning customers</span>
                      </div>

                      <div className="form-group">
                        <label>Assign to specific customers (optional)</label>
                        {assignedCustomers.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                            {assignedCustomers.map(c => (
                              <span key={c._id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#EEF2FF', color: '#3730A3', borderRadius: 14, padding: '4px 10px', fontSize: 13 }}>
                                👤 {c.name || 'Customer'}{c.phone ? ` · ${c.phone}` : ''}
                                <button type="button" onClick={() => removeAssignedCustomer(c._id)} style={{ border: 'none', background: 'transparent', color: '#3730A3', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                              </span>
                            ))}
                          </div>
                        )}
                        <input
                          type="text"
                          value={custQuery}
                          onChange={(e) => searchCustomers(e.target.value)}
                          placeholder="Search customer by name or phone…"
                          className="form-control"
                          autoComplete="off"
                        />
                        {custSearching && <span className="field-hint">Searching…</span>}
                        {custResults.length > 0 && (
                          <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                            {custResults.map(c => {
                              const already = (formData.assignedUsers || []).includes(c._id);
                              return (
                                <div key={c._id}
                                  onClick={() => !already && addAssignedCustomer(c)}
                                  style={{ padding: '8px 10px', cursor: already ? 'default' : 'pointer', opacity: already ? 0.5 : 1, borderBottom: '1px solid #F3F4F6', fontSize: 14 }}>
                                  {c.name || '(no name)'} <span style={{ color: '#888' }}>· {c.phone || '—'}</span>{already ? ' ✓ added' : ''}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <span className="field-hint">
                          {assignedCustomers.length > 0
                            ? `Only these ${assignedCustomers.length} customer(s) can use this coupon; it shows in their Offers. Clear all for everyone.`
                            : 'Leave empty = available to everyone (per the audience rule above). Add customers to make it a private/personal coupon.'}
                        </span>
                      </div>

                      <div className="form-group">
                        <label>…or target by segment (bulk-add customers)</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                          {[
                            { v: 'discount_dependent', l: 'Discount-Dependent' },
                            { v: 'discount_leaning', l: 'Discount-Leaning' },
                            { v: 'mixed', l: 'Mixed' },
                            { v: 'full_price', l: 'Full-Price' },
                          ].map(o => (
                            <label key={o.v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, background: segFilter.discountSensitivity.includes(o.v) ? '#EEF2FF' : '#f3f4f6', color: segFilter.discountSensitivity.includes(o.v) ? '#3730A3' : '#333', padding: '4px 10px', borderRadius: 14, cursor: 'pointer' }}>
                              <input type="checkbox" checked={segFilter.discountSensitivity.includes(o.v)} onChange={() => toggleSegValue('discountSensitivity', o.v)} /> {o.l}
                            </label>
                          ))}
                        </div>
                        {formData.restaurantId && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8 }}>
                            <input type="checkbox" checked={segFilter.onlyRestaurantCustomers} onChange={(e) => { setSegFilter(prev => ({ ...prev, onlyRestaurantCustomers: e.target.checked })); setSegPreview(null); }} />
                            Only customers who have ordered at the selected restaurant
                          </label>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <button type="button" onClick={previewSegment} disabled={segLoading || (!segFilter.discountSensitivity.length && !segFilter.rfmSegment.length)}
                            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #6366F1', background: '#fff', color: '#6366F1', cursor: 'pointer' }}>
                            {segLoading ? 'Resolving…' : 'Preview count'}
                          </button>
                          {segPreview && (
                            <>
                              <span style={{ fontSize: 13 }}><b>{segPreview.count}</b> customer(s) match</span>
                              {segPreview.count > 0 && (
                                <button type="button" onClick={addSegmentCustomers}
                                  style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#10B981', color: '#fff', cursor: 'pointer' }}>
                                  ➕ Add these {segPreview.count}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        <span className="field-hint">
                          e.g. tick “Discount-Dependent”, pick a restaurant above + “only its customers”, Preview, then Add — targets only that restaurant’s discount-dependent customers (the uplift play). Added customers show as chips above.
                        </span>
                      </div>

                      <div className="form-group">
                        <label>Max uses per user (empty = unlimited)</label>
                        <input
                          type="number"
                          name="perUserLimit"
                          value={formData.perUserLimit}
                          onChange={handleInputChange}
                          placeholder="e.g., 3"
                          className="form-control"
                          min="1"
                        />
                        <span className="field-hint">Overrides "one-time per user" when set</span>
                      </div>

                      <div className="form-group">
                        <label>Minimum items in cart (optional)</label>
                        <input
                          type="number"
                          name="minItems"
                          value={formData.minItems}
                          onChange={handleInputChange}
                          placeholder="e.g., 2"
                          className="form-control"
                          min="0"
                        />
                      </div>

                      <div className="form-group">
                        <label>Payment methods (empty = all)</label>
                        <div className="chip-row">
                          {['cash', 'card', 'upi'].map(m => (
                            <label key={m} className={`chip ${formData.paymentMethods.includes(m) ? 'chip-on' : ''}`}>
                              <input
                                type="checkbox"
                                checked={formData.paymentMethods.includes(m)}
                                onChange={() => toggleArrayValue('paymentMethods', m)}
                              />
                              {m.toUpperCase()}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Active days (empty = every day)</label>
                        <div className="chip-row">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, idx) => (
                            <label key={d} className={`chip ${formData.activeDays.includes(idx) ? 'chip-on' : ''}`}>
                              <input
                                type="checkbox"
                                checked={formData.activeDays.includes(idx)}
                                onChange={() => toggleArrayValue('activeDays', idx)}
                              />
                              {d}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Time window (24h, optional)</label>
                        <div className="hour-row">
                          <input
                            type="number"
                            name="startHour"
                            value={formData.startHour}
                            onChange={handleInputChange}
                            placeholder="From (0-23)"
                            className="form-control"
                            min="0"
                            max="23"
                          />
                          <span className="hour-sep">to</span>
                          <input
                            type="number"
                            name="endHour"
                            value={formData.endHour}
                            onChange={handleInputChange}
                            placeholder="To (0-23)"
                            className="form-control"
                            min="0"
                            max="23"
                          />
                        </div>
                        <span className="field-hint">e.g. 11–15 for a lunch deal. Leave empty for all day.</span>
                      </div>

                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="autoApply"
                            checked={formData.autoApply}
                            onChange={handleInputChange}
                            className="checkbox-input"
                          />
                          <span className="checkbox-text">Auto-apply when eligible</span>
                        </label>
                        <span className="field-hint">Suggest/apply this coupon automatically in the cart</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
                  </button>
                  <button type="button" onClick={resetForm} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Message */}
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Analytics Panel */}
          {showAnalytics && (
            <div className="coupon-card analytics-panel">
              <h3>Coupon Analytics (ROI)</h3>
              {analyticsLoading && <p>Loading analytics…</p>}
              {!analyticsLoading && analytics && (
                <>
                  <div className="analytics-summary">
                    <div className="analytics-stat"><span className="a-label">Redemptions</span><span className="a-value">{analytics.summary.totalRedemptions}</span></div>
                    <div className="analytics-stat"><span className="a-label">Total discount given</span><span className="a-value">₹{analytics.summary.totalDiscount}</span></div>
                    <div className="analytics-stat"><span className="a-label">Platform-funded cost</span><span className="a-value">₹{analytics.summary.platformFunded}</span></div>
                    <div className="analytics-stat"><span className="a-label">Restaurant-funded cost</span><span className="a-value">₹{analytics.summary.restaurantFunded}</span></div>
                    <div className="analytics-stat"><span className="a-label">Gross order value (with coupon)</span><span className="a-value">₹{analytics.summary.grossOrderValue}</span></div>
                  </div>
                  <div className="analytics-table-wrap">
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th>Code</th><th>Scope / Funded by</th><th>Redemptions</th><th>Unique users</th><th>Discount given</th>
                          <th>Platform cost</th><th>Restaurant cost</th><th>Avg order value</th><th>Used / limit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.perCoupon.map((c) => (
                          <tr key={c.code}>
                            <td><strong>{c.code}</strong>{!c.isActive && <span className="a-inactive"> (inactive)</span>}</td>
                            <td style={{ fontSize: '12px', lineHeight: 1.5 }}>
                              <div>{c.isUniversal ? 'Universal' : `🏪 ${c.restaurantName || 'Restaurant'}`}</div>
                              <span style={c.fundedBy === 'platform'
                                ? { background: '#e0e7ff', color: '#3730a3', padding: '1px 6px', borderRadius: '4px' }
                                : c.fundedBy === 'shared'
                                  ? { background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: '4px' }
                                  : { background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: '4px' }}>
                                {c.fundedBy === 'platform' ? 'Platform-funded'
                                  : c.fundedBy === 'shared' ? `Shared (R ${c.restaurantSharePercent != null ? c.restaurantSharePercent : 50}%)`
                                  : 'Restaurant-funded'}
                              </span>
                            </td>
                            <td>{c.redemptions}</td>
                            <td>{c.uniqueUsers}</td>
                            <td>₹{c.totalDiscount}</td>
                            <td>₹{c.platformFunded}</td>
                            <td>₹{c.restaurantFunded}</td>
                            <td>₹{c.avgOrderValue}</td>
                            <td>{c.usedCount}{c.usageLimit ? ` / ${c.usageLimit}` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Coupons List */}
          <div className="coupon-card">
            <h2>All Coupons ({coupons.length})</h2>
            <p className="card-description">
              {filter === 'all' ? 'Showing all coupons' :
               filter === 'universal' ? 'Showing universal coupons only' :
               'Showing restaurant-specific coupons only'}
            </p>

            {coupons.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🎫</div>
                <p>No coupons found</p>
              </div>
            ) : (
              <div className="coupon-list">
                {coupons.map(coupon => (
                  <div
                    key={coupon._id}
                    className={`coupon-item ${coupon.isActive ? 'active' : 'inactive'} ${isExpired(coupon.validUntil) ? 'expired' : ''}`}
                  >
                    <div className="coupon-main">
                      <div className="coupon-header">
                        <span className="coupon-code">{coupon.code}</span>
                        <div className="coupon-badges">
                          <span
                            className={`badge ${coupon.isUniversal ? 'badge-universal' : 'badge-restaurant'}`}
                            title={(coupon.restaurantIds && coupon.restaurantIds.length)
                              ? coupon.restaurantIds.map(r => r?.name || r).join(', ')
                              : undefined}
                          >
                            {coupon.isUniversal
                              ? 'Universal'
                              : (coupon.restaurantIds && coupon.restaurantIds.length)
                                ? `🏪 ${coupon.restaurantIds.length} restaurants`
                                : `🏪 ${coupon.restaurantId?.name || 'Restaurant'}`}
                          </span>
                          {(() => {
                            const funded = coupon.fundedBy || (coupon.isUniversal ? 'platform' : 'restaurant');
                            return (
                              <span
                                className="badge"
                                style={funded === 'platform'
                                  ? { background: '#e0e7ff', color: '#3730a3' }
                                  : funded === 'shared'
                                    ? { background: '#dcfce7', color: '#166534' }
                                    : { background: '#fef3c7', color: '#92400e' }}
                              >
                                {funded === 'platform' ? 'Platform-funded'
                                  : funded === 'shared' ? `Shared · R ${coupon.restaurantSharePercent != null ? coupon.restaurantSharePercent : 50}% / P ${100 - (coupon.restaurantSharePercent != null ? coupon.restaurantSharePercent : 50)}%`
                                  : 'Restaurant-funded'}
                              </span>
                            );
                          })()}
                          <span className={`badge ${coupon.isActive ? 'badge-active' : 'badge-inactive'}`}>
                            {coupon.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {coupon.isFirstOrderFreeDelivery && (
                            <span className="badge badge-first-order">🚀 First Order Free Delivery</span>
                          )}
                          {coupon.oneTimePerUser && !coupon.isFirstOrderFreeDelivery && (
                            <span className="badge badge-one-time">One-time/User</span>
                          )}
                          {Array.isArray(coupon.assignedUsers) && coupon.assignedUsers.length > 0 && (
                            <span className="badge" style={{ background: '#EEF2FF', color: '#3730A3' }}
                              title={coupon.assignedUsers.map(u => (u && (u.name || u.phone)) || '').filter(Boolean).join(', ')}>
                              👤 {coupon.assignedUsers.length} customer{coupon.assignedUsers.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {isExpired(coupon.validUntil) && (
                            <span className="badge badge-expired">Expired</span>
                          )}
                        </div>
                      </div>

                      <p className="coupon-description">{coupon.description}</p>

                      {coupon.restaurantId && (
                        <p className="coupon-restaurant">
                          Restaurant: {coupon.restaurantId.name || coupon.restaurantId}
                        </p>
                      )}

                      <div className="coupon-details">
                        <div className="detail-item">
                          <span className="detail-label">Discount:</span>
                          <span className="detail-value">
                            {coupon.discountType === 'percentage'
                              ? `${coupon.discountValue}%`
                              : `Rs.${coupon.discountValue}`}
                            {coupon.maxDiscount && ` (Max: Rs.${coupon.maxDiscount})`}
                          </span>
                        </div>

                        <div className="detail-item">
                          <span className="detail-label">Min Order:</span>
                          <span className="detail-value">Rs.{coupon.minOrderValue}</span>
                        </div>

                        {coupon.maxOrderValue > 0 && (
                          <div className="detail-item">
                            <span className="detail-label">Max Order:</span>
                            <span className="detail-value">Rs.{coupon.maxOrderValue}</span>
                          </div>
                        )}

                        <div className="detail-item">
                          <span className="detail-label">Valid:</span>
                          <span className="detail-value">
                            {new Date(coupon.validFrom).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} - {new Date(coupon.validUntil).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </span>
                        </div>

                        <div className="detail-item">
                          <span className="detail-label">Usage:</span>
                          <span className="detail-value">
                            {coupon.usedCount} {coupon.usageLimit ? `/ ${coupon.usageLimit}` : '/ Unlimited'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="coupon-actions">
                      <button
                        onClick={() => handleToggleStatus(coupon._id)}
                        className={`btn btn-sm ${coupon.isActive ? 'btn-warning' : 'btn-success'}`}
                      >
                        {coupon.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleEdit(coupon)}
                        className="btn btn-sm btn-secondary"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(coupon._id)}
                        className="btn btn-sm btn-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="info-box">
            <h3>Coupon Information</h3>
            <ul>
              <li><strong>🚀 First Order Free Delivery:</strong> A special coupon that automatically gives free delivery to each user on their first order. This coupon auto-applies at checkout and is one-time per user.</li>
              <li><strong>Universal Coupons:</strong> Apply to all restaurants and can be used by any customer on any restaurant order.</li>
              <li><strong>Restaurant Coupons:</strong> Created by individual restaurants and only apply to their orders.</li>
              <li><strong>Priority:</strong> When a customer enters a code, the system first checks for restaurant-specific coupons, then universal coupons.</li>
              <li><strong>Usage Limit:</strong> Set a limit to control how many times a coupon can be used across all orders.</li>
              <li><strong>One-time per User:</strong> When enabled, each user can only see and use this coupon once ever. Once used, the coupon won't appear for that user again.</li>
              <li><strong>Percentage vs Fixed:</strong> Percentage discounts can have a max cap (e.g., 20% up to Rs.200 max).</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponManagement;
